import { Button } from "antd";
import { useEffect, useState } from "react";
import doctorTatvaLogo from "./../../assets/images/doctor-tatva-logo.svg";
import downloadStep1 from "./../../assets/images/downloadstep1.svg";
import downloadStep2 from "./../../assets/images/downloadstep2.svg";
import downloadStep3 from "./../../assets/images/downloadstep3.svg";
import safariShare from "./../../assets/images/safari-share.svg";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: string }>;
}

const Download = () => {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isIos, setIsIos] = useState(false);
  const [isSafari, setIsSafari] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);
  const [installChecked, setInstallChecked] = useState(false);

  useEffect(() => {
    // Check if already installed as standalone
    const isInStandaloneMode = () => {
      const standalone =
        window.matchMedia("(display-mode: standalone)").matches ||
        (window.navigator as any).standalone === true ||
        document.referrer.includes("android-app://");
      return standalone;
    };

    setIsStandalone(isInStandaloneMode());

    // Simplified iOS detection (if you only need to detect iOS devices)
    const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent);

    // Simplified Safari detection (if you only need Safari vs non-Safari)
    const isSafari = () =>
      /safari/i.test(navigator.userAgent) &&
      !/chrome|chromium|crios/i.test(navigator.userAgent);

    setIsSafari(isSafari());
    setIsIos(isIos());

    // Handler for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Store the event for later use
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      // Show the install button
      setIsInstallable(true);
    };

    // Add the event listener
    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // Set a timeout to check installation availability
    const timer = setTimeout(() => {
      setInstallChecked(true);
    }, 2000);

    // Cleanup
    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      clearTimeout(timer);
    };
  }, []);

  // Additional listener for appinstalled event
  useEffect(() => {
    const handleAppInstalled = () => {
      // App was installed
      console.log("App was installed");
      setIsInstallable(false);
      setDeferredPrompt(null);
      setIsStandalone(true);
    };

    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleAddToHomeScreenClick = async () => {
    if (!deferredPrompt) {
      console.log("Installation prompt not available");
      return;
    }

    try {
      // Show the prompt
      await deferredPrompt.prompt();

      // Wait for the user to respond to the prompt
      const choiceResult = await deferredPrompt.userChoice;

      if (choiceResult.outcome === "accepted") {
        console.log("User accepted the installation prompt");
      } else {
        console.log("User dismissed the installation prompt");
      }

      // Clear the saved prompt since it can't be used again
      setDeferredPrompt(null);
      setIsInstallable(false);
    } catch (error) {
      console.error("Error during installation prompt:", error);
    }
  };

  // If already installed, show a different message
  if (isStandalone) {
    return (
      <div className="w-full absolute top-[18%] left-0">
        <div className="mx-auto max-w-[400px] w-full px-4 flex flex-col gap-y-4 items-center">
          <img
            src={doctorTatvaLogo}
            width={84}
            height={84}
            alt="doctor-tatva-logo"
            loading="lazy"
          />
          <div className="text-xl font-medium text-[#454551]">
            App is already installed!
          </div>
          <div className="text-base text-[#454551] text-center">
            You can close this window and use the app from your home screen.
          </div>
          <Button
            type="link"
            onClick={() => setIsStandalone(false)}
            className="mt-2"
          >
            Not installed? Click here
          </Button>
        </div>
      </div>
    );
  }

  // Render appropriate UI based on platform
  const renderInstallUI = () => {
    if (isIos || isSafari) {
      return (
        <div className={`relative left-0 right-0 `}>
          <div className="mx-auto max-w-[380px] px-2 flex flex-col justify-between items-center">
            <div className="w-full flex flex-col justify-center bg-[#4b4ad50d] rounded-[10px] leading-[24px] p-5 z-10">
              <div className="text-base text text-left text-[#3D4854] font-[650]">
                Add Tatvacare app on your device
              </div>

              <div className="text-left flex mb-2 mt-4">
                <span className="inline-block mr-2 bg-[#4b4ad5] text-white font-[700] text-xs text-center px-2 py-1 rounded-[6px]">
                  1
                </span>
                <span className="flex gap-x-2 w-full text-base text-[#3D4854]">
                  Click on
                  <img src={safariShare} alt="safariShare" /> button on your
                  browser
                </span>
              </div>

              <div className="items-center flex mb-2">
                <div className="h-16 border-l-2 border-dashed border-[#4b4ad5]"></div>
                <img src={downloadStep1} alt="Step 1" className="mr-5 ml-5" />
              </div>

              <div className="items-center flex mb-2 mt-4">
                <span className="inline-block mr-2 mb-4 bg-[#4b4ad5] text-white font-[700] text-xs text-center px-2 py-1 rounded-[6px]">
                  2
                </span>
                <span className="text-base w-full text-left text-[#3D4854]">
                  In the popup menu actions, tap on <br />
                  <span className="font-poppins text-[14px] font-bold leading-[21px] text-left decoration-skip-ink-none">
                    "Add to Home Screen"
                  </span>{" "}
                  option
                </span>
              </div>

              <div className="items-center flex mb-2">
                <div className="h-16 border-l-2 border-dashed border-[#4b4ad5]"></div>
                <img src={downloadStep2} alt="Step 2" className="mr-5 ml-5" />
              </div>

              <div className="items-center justify-between flex mb-2 mt-4">
                <span className="inline-block mr-2 mb-4 bg-[#4b4ad5] text-white font-[700] text-xs text-center px-2 py-1 rounded-[6px]">
                  3
                </span>
                <span className="text-base text-left text-[#3D4854]">
                  Click on the{" "}
                  <span className="font-poppins text-[14px] font-bold leading-[21px] text-left decoration-skip-ink-none">
                    "Add"
                  </span>{" "}
                  button in the top right corner
                </span>
              </div>

              <div className="items-center flex mb-2">
                <div className="h-16 border-l-2 border-dashed border-[#4b4ad50d]"></div>
                <img src={downloadStep3} alt="Step 3" className="mr-5 ml-5" />
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      // Android/Chrome/Other browsers install UI
      return (
        <Button
          type="primary"
          className="w-full h-14 rounded-2xl bg-[#4B4AD5] p-5 text-lg font-medium mt-4"
          onClick={handleAddToHomeScreenClick}
          disabled={!isInstallable}
        >
          {isInstallable
            ? "Install Tatvacare App"
            : installChecked
            ? "Installation not available in this browser"
            : "Loading installation options..."}
        </Button>
      );
    }
  };

  return (
    <div
      className={
        !(isIos || isSafari)
          ? `w-full absolute top-[18%] left-0`
          : `w-full absolute top-[5%] left-0`
      }
    >
      <div className="mx-auto max-w-[400px] w-full px-4 flex flex-col gap-y-4 items-center">
        <img
          src={doctorTatvaLogo}
          width={84}
          height={84}
          alt="doctor-tatva-logo"
          loading="lazy"
        />
        <div className="text-xs font-medium text-[#454551]">
          Remote care for your patients
        </div>

        {renderInstallUI()}
      </div>
    </div>
  );
};

export default Download;
